import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { environment } from '../../../../environments/environment';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { AuthService } from '../../../core/auth/auth.service';
import { canAccess, DEPARTMENT, ROLE_LEVEL } from '../../../core/auth/access-control';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { COUNTRY_OPTIONS } from '../../../shared/constants/countries.constant';
import {
  SaveTourRequest,
  TourItem,
  TourService,
  TourStatus,
  TourType,
} from '../services/tour.service';

interface TourRow {
  id: string;
  tourNo: string;
  code: string;
  name: string;
  type: TourType;
  travelDate: string;
  returnDate: string;
  currentPax: number;
  requiredPax: number;
  bookingCount: number;
  hasBookingPayments: boolean;
  profilePath: string | null;
  status: TourStatus;
}

type ExpenseItemForm = FormGroup<{
  expenseTypeId: FormControl<string>;
  amount: FormControl<number | null>;
}>;

function tourFinancialLimits(control: AbstractControl): ValidationErrors | null {
  const tourPrice = control.get('tourPrice');
  const requiredPax = control.get('requiredPax');
  const expenses = control.get('expenses') as FormArray<ExpenseItemForm> | null;

  if (!tourPrice?.valid || !requiredPax?.valid || !expenses) {
    return null;
  }

  const grossTourTotalInCents =
    Math.round(Number(tourPrice.value) * 100) * Number(requiredPax.value);

  const expenseTotalInCents = expenses.controls.reduce(
    (total, expense) => total + Math.round(Number(expense.controls.amount.value ?? 0) * 100),
    0,
  );
  return expenseTotalInCents > grossTourTotalInCents ? { expensesExceedTourTotal: true } : null;
}

@Component({
  selector: 'app-tours-page',
  imports: [
    DatePickerModule,
    DatePipe,
    DecimalPipe,
    FileUploadModule,
    InputNumberModule,
    InputTextModule,
    LucideAngularModule,
    ReactiveFormsModule,
    RefreshButtonComponent,
    RouterLink,
    SelectModule,
    StatusBadge,
    TableModule,
    TextareaModule,
  ],
  templateUrl: './tours-page.component.html',
})
export class ToursPageComponent implements OnInit {
  private readonly auth = inject(AuthService);

  readonly canManageTours = computed(() =>
    canAccess(this.auth.user(), {
      levels: [ROLE_LEVEL.MANAGER, ROLE_LEVEL.EMPLOYEE],
      departments: [DEPARTMENT.SALES],
    }),
  );

  readonly canViewTourActions = computed(() =>
    canAccess(this.auth.user(), {
      levels: [ROLE_LEVEL.MANAGER, ROLE_LEVEL.EMPLOYEE],
      departments: [DEPARTMENT.SALES, DEPARTMENT.ACCOUNTING],
    }),
  );

  canModifyTour(row: TourRow): boolean {
    return this.canManageTours() && (this.auth.user()?.role.level === 1 || !row.hasBookingPayments);
  }

  private readonly fb = inject(FormBuilder);
  private readonly tourService = inject(TourService);
  private readonly overlay = inject(GlobalOverlayService);

  readonly isCreateModalOpen = signal(false);
  readonly isSubmitAttempted = signal(false);
  readonly searchTerm = signal('');
  readonly profileFileName = signal('');
  readonly tourPdfFileName = signal('');
  readonly profileFile = signal<File | null>(null);
  readonly tourPdfFile = signal<File | null>(null);
  readonly isLoadingTours = signal(false);
  readonly isSavingTour = signal(false);
  readonly editingTourId = signal<string | null>(null);
  readonly existingProfilePath = signal<string | null>(null);
  readonly existingTourPdfPath = signal<string | null>(null);

  readonly tourTypeOptions = [
    { label: 'จอยทัวร์', value: 'JOIN_TOUR' as TourType },
    { label: 'เหมากรุ๊ป', value: 'GROUP_TOUR' as TourType },
  ];

  readonly countryOptions = COUNTRY_OPTIONS.map((country) => ({
    label: country.label,
    value: country.label,
  }));

  readonly expenseTypeOptions = signal<Array<{ label: string; value: string }>>([]);
  readonly isLoadingExpenseTypes = signal(false);
  readonly expenseTypeLoadError = signal('');

  readonly expenseItems = this.fb.array([this.createExpenseItem()]);

  readonly tourForm = this.fb.nonNullable.group(
    {
      tourCode: ['', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(30)]],
      tourName: ['', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(200)]],
      country: ['', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(100)]],
      tourType: this.fb.nonNullable.control<TourType>('JOIN_TOUR', Validators.required),
      travelDate: this.fb.control<Date | null>(null, Validators.required),
      returnDate: this.fb.control<Date | null>(null),
      tourPrice: [0, [Validators.required, Validators.min(0.01)]],
      estimatedExpense: [0, [Validators.required, Validators.min(0.01)]],
      saleCommission: [0, [Validators.required, Validators.min(0)]],
      administratorCommissionPercent: [
        0,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      programDetail: ['', Validators.maxLength(5000)],
      profilePath: ['', Validators.maxLength(500)],
      tourPdfDetailPath: ['', Validators.maxLength(500)],
      requiredPax: [1, [Validators.required, Validators.min(1)]],
      note: ['', Validators.maxLength(1000)],
      expenses: this.expenseItems,
    },
    { validators: tourFinancialLimits },
  );

  private readonly assetBaseUrl = environment.apiUrl.replace(/\/api\/?$/, '');

  readonly tours = signal<TourRow[]>([]);

  readonly filteredTours = computed(() => {
    const query = this.searchTerm().trim().toLowerCase();

    return this.tours().filter((tour) => {
      return (
        !query ||
        [tour.tourNo, tour.code, tour.name].some((value) => value.toLowerCase().includes(query))
      );
    });
  });

  ngOnInit(): void {
    void this.loadTours();
  }

  async loadTours(): Promise<void> {
    if (this.isLoadingTours()) return;
    this.isLoadingTours.set(true);
    this.overlay.showLoading('กำลังโหลดรายการทัวร์...');
    try {
      const tours = await this.tourService.getAll();
      this.tours.set(tours.map((tour) => this.toTourRow(tour)));
    } catch (error: unknown) {
      this.overlay.error(
        'โหลดรายการทัวร์ไม่สำเร็จ',
        this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'),
      );
    } finally {
      this.isLoadingTours.set(false);
      this.overlay.hideLoading();
    }
  }

  setSearchTerm(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  onTourTypeChange(): void {
    if (this.tourForm.controls.tourType.value === 'GROUP_TOUR') {
      this.tourForm.controls.saleCommission.setValue(0);
    }
  }

  openCreateModal(): void {
    this.editingTourId.set(null);
    this.existingProfilePath.set(null);
    this.existingTourPdfPath.set(null);
    this.isSubmitAttempted.set(false);
    this.profileFileName.set('');
    this.tourPdfFileName.set('');
    this.profileFile.set(null);
    this.tourPdfFile.set(null);
    this.expenseTypeOptions.set([]);
    this.expenseTypeLoadError.set('');
    this.tourForm.reset({
      tourCode: '',
      tourName: '',
      country: '',
      tourType: 'JOIN_TOUR',
      travelDate: null,
      returnDate: null,
      tourPrice: 0,
      estimatedExpense: 0,
      saleCommission: 0,
      administratorCommissionPercent: 0,
      programDetail: '',
      profilePath: '',
      tourPdfDetailPath: '',
      requiredPax: 1,
      note: '',
    });
    this.expenseItems.clear();
    this.expenseItems.push(this.createExpenseItem());
    this.isCreateModalOpen.set(true);
    void this.loadExpenseTypes();
  }

  async openEditModal(row: TourRow): Promise<void> {
    if (!this.canModifyTour(row)) {
      this.overlay.error('ไม่สามารถแก้ไขทัวร์ได้', 'ทัวร์ที่มีการชำระเงิน แก้ไขได้เฉพาะ Admin');
      return;
    }

    this.isSubmitAttempted.set(false);
    this.expenseTypeOptions.set([]);
    this.expenseTypeLoadError.set('');
    this.overlay.showLoading('กำลังโหลดข้อมูลทัวร์...');
    try {
      const [tour] = await Promise.all([this.tourService.getById(row.id), this.loadExpenseTypes()]);
      this.editingTourId.set(tour.id);
      this.profileFile.set(null);
      this.tourPdfFile.set(null);
      this.profileFileName.set(this.fileNameFromPath(tour.profilePath));
      this.tourPdfFileName.set(this.fileNameFromPath(tour.tourPdfDetailPath));

      this.existingProfilePath.set(tour.profilePath);
      this.existingTourPdfPath.set(tour.tourPdfDetailPath);
      this.tourForm.reset({
        tourCode: tour.tourCode,
        tourName: tour.tourName,
        country: tour.country,
        tourType: tour.tourType,
        travelDate: this.parseDate(tour.travelDate),
        returnDate: this.parseDate(tour.returnDate),
        tourPrice: Number(tour.tourprice),
        estimatedExpense: Number(tour.estimatedExpense),
        saleCommission: Number(tour.com_sale),
        administratorCommissionPercent: Number(tour.com_admintrator),
        programDetail: tour.programDetail ?? '',
        profilePath: tour.profilePath ?? '',
        tourPdfDetailPath: tour.tourPdfDetailPath ?? '',
        requiredPax: tour.requiredPax ?? 1,
        note: tour.note ?? '',
      });
      this.expenseItems.clear();
      for (const expense of tour.expenseRequests) {
        this.expenseItems.push(
          this.createExpenseItem({
            expenseTypeId: expense.expenseTypeId ?? '',
            amount: expense.amount === null ? null : Number(expense.amount),
          }),
        );
      }
      if (this.expenseItems.length === 0) {
        this.expenseItems.push(this.createExpenseItem());
      }
      this.setExpenseTypeControlsDisabled(this.expenseTypeOptions().length === 0);
      this.isCreateModalOpen.set(true);
    } catch (error: unknown) {
      this.overlay.error(
        'โหลดข้อมูลทัวร์ไม่สำเร็จ',
        this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'),
      );
    } finally {
      this.overlay.hideLoading();
    }
  }

  async loadExpenseTypes(): Promise<void> {
    if (this.isLoadingExpenseTypes()) return;

    this.isLoadingExpenseTypes.set(true);
    this.expenseTypeLoadError.set('');
    this.setExpenseTypeControlsDisabled(true);
    try {
      const expenseTypes = await this.tourService.getExpenseTypes();
      this.expenseTypeOptions.set(
        expenseTypes.map((expenseType) => ({ label: expenseType.name, value: expenseType.id })),
      );
      if (expenseTypes.length === 0) {
        this.expenseTypeLoadError.set('ยังไม่มีรายการค่าใช้จ่ายที่เปิดใช้งาน');
      }
    } catch {
      this.expenseTypeOptions.set([]);
      this.expenseTypeLoadError.set('โหลดรายการค่าใช้จ่ายไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      this.isLoadingExpenseTypes.set(false);
      this.setExpenseTypeControlsDisabled(this.expenseTypeOptions().length === 0);
    }
  }

  private createExpenseItem(
    value: { expenseTypeId?: string; amount?: number | null } = {},
  ): ExpenseItemForm {
    return this.fb.nonNullable.group({
      expenseTypeId: this.fb.nonNullable.control(
        {
          value: value.expenseTypeId ?? '',
          disabled: this.isLoadingExpenseTypes() || this.expenseTypeOptions().length === 0,
        },
        Validators.required,
      ),
      amount: this.fb.control<number | null>(value.amount ?? null, [
        Validators.required,
        Validators.min(0.01),
      ]),
    });
  }

  private setExpenseTypeControlsDisabled(disabled: boolean): void {
    for (const expense of this.expenseItems.controls) {
      const control = expense.controls.expenseTypeId;
      if (disabled) {
        control.disable();
      } else {
        control.enable();
      }
    }
  }

  addExpenseItem(): void {
    if (this.expenseItems.length < this.expenseTypeOptions().length) {
      this.expenseItems.push(this.createExpenseItem());
    }
  }

  removeExpenseItem(index: number): void {
    if (this.expenseItems.length > 1) {
      this.expenseItems.removeAt(index);
    }
  }

  availableExpenseTypeOptions(index: number): Array<{ label: string; value: string }> {
    const selectedByOtherRows = new Set(
      this.expenseItems.controls
        .map((expense, rowIndex) =>
          rowIndex === index ? null : expense.controls.expenseTypeId.value,
        )
        .filter((value): value is string => !!value),
    );
    return this.expenseTypeOptions().filter((option) => !selectedByOtherRows.has(option.value));
  }

  estimatedExpenseTotal(): number {
    return this.expenseItems.controls.reduce(
      (total, expense) => total + Number(expense.controls.amount.value ?? 0),
      0,
    );
  }

  closeCreateModal(): void {
    this.isCreateModalOpen.set(false);
    this.editingTourId.set(null);
    this.existingProfilePath.set(null);
    this.existingTourPdfPath.set(null);
    this.profileFile.set(null);
    this.tourPdfFile.set(null);
  }

  selectProfileFile(event: FileSelectEvent): void {
    const file = event.files?.[0];
    this.profileFile.set(file ?? null);
    this.profileFileName.set(file?.name ?? '');
    this.tourForm.controls.profilePath.setValue(file?.name ?? '');
  }

  selectTourPdfFile(event: FileSelectEvent): void {
    const file = event.files?.[0];
    this.tourPdfFile.set(file ?? null);
    this.tourPdfFileName.set(file?.name ?? '');
    this.tourForm.controls.tourPdfDetailPath.setValue(file?.name ?? '');
  }

  clearProfileFile(): void {
    this.profileFile.set(null);
    this.profileFileName.set('');
    this.tourForm.controls.profilePath.setValue('');
  }

  clearTourPdfFile(): void {
    this.tourPdfFile.set(null);
    this.tourPdfFileName.set('');
    this.tourForm.controls.tourPdfDetailPath.setValue('');
  }

  async saveTour(): Promise<void> {
    if (this.isSavingTour()) return;
    this.onTourTypeChange();
    this.isSubmitAttempted.set(true);
    this.tourForm.controls.estimatedExpense.setValue(this.estimatedExpenseTotal());
    const { travelDate, returnDate } = this.tourForm.getRawValue();
    if (travelDate && returnDate && returnDate < travelDate) {
      this.tourForm.controls.travelDate.markAsTouched();
      this.tourForm.controls.returnDate.markAsTouched();
      this.overlay.error('วันที่เดินทางไม่ถูกต้อง', 'วันเดินทางกลับต้องไม่น้อยกว่าวันเดินทางไป');
      return;
    }
    if (this.isLoadingExpenseTypes() || this.expenseTypeOptions().length === 0) {
      this.tourForm.markAllAsTouched();
      return;
    }
    if (this.tourForm.invalid) {
      this.tourForm.markAllAsTouched();
      return;
    }

    const value = this.tourForm.getRawValue();
    const input: SaveTourRequest = {
      tourCode: value.tourCode.trim(),
      tourName: value.tourName.trim(),
      country: value.country.trim(),
      tourType: value.tourType,
      travelDate: this.toDateString(value.travelDate),
      returnDate: this.toDateString(value.returnDate) || null,
      tourPrice: value.tourPrice,
      requiredPax: value.requiredPax,
      saleCommission: value.saleCommission,
      administratorCommissionPercent: value.administratorCommissionPercent,
      programDetail: value.programDetail.trim() || null,
      profilePath: value.profilePath.trim() || null,
      tourPdfDetailPath: value.tourPdfDetailPath.trim() || null,
      note: value.note.trim() || null,
      expenses: value.expenses.map((expense) => ({
        expenseTypeId: expense.expenseTypeId,
        amount: Number(expense.amount),
      })),
    };

    const editingTourId = this.editingTourId();
    this.isSavingTour.set(true);
    this.overlay.showLoading(editingTourId ? 'กำลังแก้ไขทัวร์...' : 'กำลังสร้างทัวร์...');
    try {
      const tour = editingTourId
        ? await this.tourService.update(
            editingTourId,
            input,
            this.profileFile(),
            this.tourPdfFile(),
          )
        : await this.tourService.create(input, this.profileFile(), this.tourPdfFile());
      const tourRow = this.toTourRow(tour);
      this.tours.update((tours) =>
        editingTourId
          ? tours.map((item) => (item.id === tour.id ? tourRow : item))
          : [tourRow, ...tours],
      );
      this.closeCreateModal();
      this.overlay.success(
        editingTourId ? 'แก้ไขทัวร์สำเร็จ' : 'สร้างทัวร์สำเร็จ',
        `บันทึก ${tour.tourNo} เรียบร้อยแล้ว`,
      );
    } catch (error: unknown) {
      this.overlay.error(
        editingTourId ? 'แก้ไขทัวร์ไม่สำเร็จ' : 'สร้างทัวร์ไม่สำเร็จ',
        this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'),
      );
    } finally {
      this.isSavingTour.set(false);
    }
  }

  async deleteTour(row: TourRow): Promise<void> {
    if (!this.canModifyTour(row)) {
      this.overlay.error('ไม่สามารถลบทัวร์ได้', 'ทัวร์ที่มีการชำระเงิน ลบได้เฉพาะ Admin');
      return;
    }

    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบทัวร์',
      message: `ต้องการลบ ${row.tourNo} - ${row.name} ใช่หรือไม่? รายการจะถูกเปลี่ยนสถานะเป็นถูกลบ`,
      confirmText: 'ลบทัวร์',
      cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังลบทัวร์...');
    try {
      const deletedTour = await this.tourService.delete(row.id);
      const deletedTourRow = this.toTourRow(deletedTour);
      this.tours.update((tours) =>
        tours.map((tour) => (tour.id === deletedTour.id ? deletedTourRow : tour)),
      );
      this.overlay.success('ลบทัวร์สำเร็จ', `เปลี่ยนสถานะ ${deletedTour.tourNo} เป็นถูกลบแล้ว`);
    } catch (error: unknown) {
      this.overlay.error('ลบทัวร์ไม่สำเร็จ', this.apiErrorMessage(error, 'กรุณาลองใหม่อีกครั้ง'));
    }
  }

  private toTourRow(tour: TourItem): TourRow {
    return {
      hasBookingPayments: tour.bookings.some((booking) => booking._count.booking_payments > 0),
      id: tour.id,
      tourNo: tour.tourNo,
      code: tour.tourCode,
      name: tour.tourName,
      type: tour.tourType,
      travelDate: tour.travelDate,
      returnDate: tour.returnDate ?? tour.travelDate,
      currentPax: tour.bookings
        .filter((booking) => !['DELETED', 'REFUND'].includes(booking.status))
        .reduce((total, booking) => total + booking.pax, 0),
      requiredPax: tour.requiredPax ?? 0,
      bookingCount: tour.bookings.filter(
        (booking) => !['DELETED', 'REFUND'].includes(booking.status),
      ).length,
      profilePath: tour.profilePath,
      status: tour.status,
    };
  }

  tourImageUrl(path: string | null): string | null {
    if (!path) return null;
    if (/^(https?:)?\/\//.test(path)) return path;
    return `${this.assetBaseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  private apiErrorMessage(error: unknown, fallback: string): string {
    return error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
      ? error.error.message
      : fallback;
  }

  private toDateString(value: Date | null): string {
    if (!value) {
      return '';
    }

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseDate(value: string | null): Date | null {
    return value ? new Date(`${value.slice(0, 10)}T00:00:00`) : null;
  }

  private fileNameFromPath(value: string | null): string {
    return value?.split('/').pop() ?? '';
  }
}
